import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, Lock, Sparkles, RefreshCw, Download, TrendingUp, Percent, ArrowRight, Layers, Mail, LogIn, Car } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api';
import { TID } from '@/testIds';
import { useAudience } from '@/context/AudienceContext';
import { useAuth, TIER_RANK } from '@/context/AuthContext';
import { WrapulatorLogo } from './Brand';
import SignInModal from './SignInModal';
import VehiclePicker from './VehiclePicker';
import { UpgradePrompt, EntitlementBadge } from './TierGate';

const TIERS = [
  { id: 'guest',   label: 'Guest',   note: 'Price range · free' },
  { id: 'starter', label: 'Starter', note: 'Exact quote + breakdown · $9/mo' },
  { id: 'pro',     label: 'Pro',     note: 'Custom labor · complexity · margin · PDF · $29/mo' },
  { id: 'fleet',   label: 'Fleet',   note: 'Everything + API + batch · $79/mo' },
];

const COVERAGE_OPTS = [
  { id: 'partial', label: 'Partial', desc: 'accent / hood / roof' },
  { id: 'half',    label: 'Half',    desc: 'partial wrap' },
  { id: 'full',    label: 'Full',    desc: 'full color change' },
];

const FINISH_OPTS = [
  { id: 'solid', label: 'Solid' },
  { id: 'matte', label: 'Matte' },
  { id: 'satin', label: 'Satin' },
  { id: 'gloss_metallic', label: 'Metallic' },
  { id: 'chrome', label: 'Chrome' },
  { id: 'colorflow', label: 'ColorFlow' },
];

// Fallback so the dropdowns are populated instantly on mount and stay populated
// even if /api/wrapulator/config is slow, blocked, or momentarily down. The
// backend response overrides this the moment it arrives.
const FALLBACK_CONFIG = {
  presets: [
    { key: 'Compact Car' }, { key: 'Sedan' }, { key: 'Coupe/Sport' }, { key: 'SUV' },
    { key: 'Pickup Truck' }, { key: 'Cargo Van' }, { key: 'Sprinter/HD' },
    { key: 'Box Truck' }, { key: 'Trailer' },
    { key: 'Wall (per sqft)' }, { key: 'Floor (per sqft)' }, { key: 'Window (per sqft)' },
  ],
  materials: [
    { key: '3m_1080',   label: '3M 1080 Series',   cost_per_sqft: 6.50 },
    { key: '3m_2080',   label: '3M 2080 Series',   cost_per_sqft: 7.50 },
    { key: 'avery',     label: 'Avery SW900',      cost_per_sqft: 6.75 },
    { key: 'orafol',    label: 'Orafol 970RA',     cost_per_sqft: 6.25 },
    { key: 'aura_ppf',  label: 'Aura PPF',         cost_per_sqft: 12.00 },
    { key: 'hexis',     label: 'Hexis Skintac',    cost_per_sqft: 6.00 },
    { key: 'mactac',    label: 'Mactac Wall/Floor', cost_per_sqft: 3.75 },
  ],
  wholesale_skus: [
    { code: 'FLT-SM',  label: 'Compact commercial / coupe' },
    { code: 'FLT-MD',  label: 'Standard cargo / mid van' },
    { code: 'FLT-LG',  label: 'Extended / high-roof' },
    { code: 'FLT-BOX', label: 'Box truck 14ft-26ft' },
    { code: 'SQF-FLT', label: 'Flat / architectural' },
    { code: 'SQF-COM', label: 'Commercial fleet contours' },
    { code: 'SQF-CLR', label: 'Color-change restyling' },
  ],
};

const fmtUSD = (n) =>
  n?.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) ?? '$0.00';

const fmtRange = (lo, hi) => (lo === hi ? fmtUSD(lo) : `${fmtUSD(lo)} – ${fmtUSD(hi)}`);

export default function Wrapulator() {
  const { isB2B } = useAudience();
  const { isPartner, isAuthed, entitledTier, entitlement } = useAuth();
  // In Retail audience, only signed-in Partners may access Wholesale mode.
  const wholesaleLocked = !isB2B && !isPartner;
  // Anonymous visitors can see the whole tool but can't run quotes.
  const [signInOpen, setSignInOpen] = useState(false);
  // Locked tier the user tried to pick → shows the upgrade / trial-pass prompt.
  const [upgradeFor, setUpgradeFor] = useState(null);

  const [mode, setMode] = useState(isB2B ? 'wholesale' : 'retail');
  useEffect(() => {
    // If audience flips to Retail and user isn't a Partner, force retail mode.
    setMode(isB2B ? 'wholesale' : 'retail');
  }, [isB2B]);

  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [tier, setTier] = useState('guest');
  const [preset, setPreset] = useState('Sedan');
  const [material, setMaterial] = useState('3m_2080');
  const [coverage, setCoverage] = useState('full');
  const [finish, setFinish] = useState('matte');
  const [labor, setLabor] = useState(95);
  const [qty, setQty] = useState(1);
  const [complexity, setComplexity] = useState(1.0);
  const [margin, setMargin] = useState(0);

  // wholesale inputs
  const [difficulty, setDifficulty] = useState(0);
  const [linearFt, setLinearFt] = useState(20);
  const [printCost, setPrintCost] = useState(750);
  const [retailPrice, setRetailPrice] = useState(3800);
  const [sqftOverride, setSqftOverride] = useState(100);
  const [skuOverride, setSkuOverride] = useState('');  // '' = use PRESET_TO_SKU mapping
  const [vehicleSel, setVehicleSel] = useState({ vehicle: null, includeRoof: true }); // exact vehicle from PVO DB
  const [showVehicleDb, setShowVehicleDb] = useState(false);

  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    api.get('/wrapulator/config')
      .then(({ data }) => setConfig(data))
      .catch(() => {
        // Keep fallback config so the dropdowns still work; warn quietly.
        console.warn('[Wrapulator] Live config unavailable — using fallback presets/materials.');
      });
  }, []);

  const isPro = tier === 'pro' || tier === 'fleet';
  const isGuest = tier === 'guest';
  const isFree = tier === 'guest' || tier === 'starter';
  const isSqftPreset = preset && preset.includes('per sqft');
  const isBox = preset === 'Box Truck';

  // Follow the account's entitlement: paid/trial accounts land on their tier,
  // and nobody can sit on a tier above what they're entitled to (server clamps too).
  useEffect(() => {
    setTier(entitledTier);
    setUpgradeFor(null);
  }, [entitledTier]);

  const pickTier = (id) => {
    if (TIER_RANK[id] > TIER_RANK[entitledTier]) { setUpgradeFor(id); return; }
    setUpgradeFor(null);
    setTier(id);
  };

  const downloadProposal = async () => {
    try {
      const { data } = await api.post('/wrapulator/proposal', { calc: result }, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a'); a.href = url; a.download = 'motorinks-proposal.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Proposal downloaded.');
    } catch (e) {
      if (e?.response?.status === 403) { setUpgradeFor('pro'); toast.info('Branded PDF is a Pro feature'); return; }
      toast.error('Could not build proposal PDF');
    }
  };

  // Enforce labor lock at $95/hr for Guest + Starter tiers (server also enforces).
  useEffect(() => {
    if (isFree && labor !== 95) setLabor(95);
  }, [isFree, labor]);

  const doCalculate = async () => {
    setCalculating(true);
    try {
      const payload = {
        preset, material, coverage, finish,
        labor_rate: isFree ? 95 : labor, quantity: qty, tier, mode,
      };
      if (mode === 'retail') {
        if (isPro) {
          payload.complexity_multiplier = complexity;
          payload.margin_percent = margin;
        }
      } else {
        payload.difficulty_modifier_percent = difficulty;
        if (isBox) payload.linear_feet = linearFt;
        if (isSqftPreset) payload.sqft_override = sqftOverride;
        if (retailPrice > 0) payload.retail_sell_price = retailPrice;
        if (printCost >= 0) payload.partner_print_cost = printCost;
        if (skuOverride) payload.sku_override = skuOverride;
        if (showVehicleDb && vehicleSel.vehicle) {
          payload.vehicle_id = vehicleSel.vehicle.id;
          payload.include_roof = vehicleSel.includeRoof;
        }
      }
      const { data } = await api.post('/wrapulator/calculate', payload);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    if (!config) return;
    // Don't run live calcs for anonymous visitors — panel shows sign-in overlay instead.
    if (!isAuthed) { setResult(null); return; }
    const t = setTimeout(doCalculate, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, config, mode, preset, material, coverage, finish, labor, qty, tier, complexity, margin, difficulty, linearFt, printCost, retailPrice, sqftOverride, skuOverride, vehicleSel, showVehicleDb]);

  const presetsList = useMemo(() => (config ? config.presets.map((p) => p.key) : []), [config]);
  const materialsList = useMemo(() => (config ? config.materials : []), [config]);
  const wholesaleSkus = useMemo(() => (config?.wholesale_skus || []), [config]);
  const openMembership = () => document.getElementById('membership')?.scrollIntoView({ behavior: 'smooth' });

  const isWholesale = mode === 'wholesale';

  // Save/email quote handler — pre-fills the QuoteSection form with the current calc.
  const saveQuote = () => {
    const summary = isWholesale
      ? `Wrapulator wholesale quote:\n- SKU: ${result?.sku_code}\n- Service: ${result?.sku_label}\n- Labor range: ${result?.grand_total_low}–${result?.grand_total_high} USD\n- Timeline: ${result?.sku_timeline}\n- Quantity: ${result?.quantity}`
      : `Wrapulator retail quote:\n- Preset: ${result?.preset}\n- Material: ${result?.material}\n- Coverage: ${result?.coverage} · Finish: ${result?.finish}\n- Grand total: $${result?.grand_total?.toLocaleString?.() || '?'}\n- Quantity: ${result?.quantity}`;
    try {
      sessionStorage.setItem('mi_prefill_quote', summary);
    } catch {}
    // Dispatch a live event so an already-mounted QuoteSection picks it up
    // without needing a page reload.
    try {
      window.dispatchEvent(new CustomEvent('mi:prefill-quote', { detail: summary }));
    } catch {}
    const q = document.getElementById('quote');
    if (q) q.scrollIntoView({ behavior: 'smooth', block: 'start' });
    toast.success('Quote pre-filled below — add your contact details to send.');
  };

  return (
    <section id="wrapulator" className="relative py-24 lg:py-32 border-b mi-line overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1666555038185-8323c553de64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxicnVzaGVkJTIwbWV0YWwlMjBkYXJrJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODU4MDA2MjZ8MA&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <WrapulatorLogo variant="primary" height={72}
                            className="mb-4 drop-shadow-[0_4px_16px_rgba(230,57,70,0.3)]" />
            <div className="mi-eyebrow mb-3 flex items-center gap-2">
              <Calculator className="w-3 h-3" /> // WRAPULATOR · v2 · BY MOTOR iNKS
            </div>
            <h2 className="mi-headline text-5xl md:text-6xl lg:text-7xl">
              Real quotes.<br />
              <span className="text-[#E63946]">Real math.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/70 leading-relaxed">
            Retail mode ballparks any wrap in seconds. Wholesale mode prices at partner rates and previews
            your margin against a retail sell price.
          </p>
        </div>

        {/* Mode toggle */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex items-stretch border mi-line-strong bg-black/70">
            <button
              onClick={() => setMode('retail')} data-testid={TID.wrapModeRetail}
              className={`px-4 py-2 mi-headline text-sm ${mode === 'retail' ? 'bg-[#E63946] text-white' : 'text-white/70'}`}
            >
              Retail
            </button>
            <button
              onClick={() => {
                if (wholesaleLocked) {
                  toast.info('Wholesale pricing is for B2B partners. Sign in with your Partner account to unlock.');
                  return;
                }
                setMode('wholesale');
              }}
              data-testid={TID.wrapModeWholesale}
              aria-disabled={wholesaleLocked}
              className={`px-4 py-2 mi-headline text-sm inline-flex items-center gap-1.5 ${
                mode === 'wholesale' ? 'bg-[#E63946] text-white' : 'text-white/70'
              } ${wholesaleLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {wholesaleLocked && <Lock className="w-3.5 h-3.5" />}
              Wholesale · B2B
            </button>
          </div>
          {wholesaleLocked && (
            <span className="mi-tag text-[10px] inline-flex items-center gap-1 border-yellow-400/50 text-yellow-400/90">
              <Lock className="w-3 h-3" /> PARTNERS ONLY — SIGN IN TO UNLOCK
            </span>
          )}
          {isWholesale && (
            <span className="mi-tag mi-tag-red text-[10px]">
              LABOR-ONLY · YOU SUPPLY MEDIA
            </span>
          )}
          {isWholesale && <BulkTierBadge result={result} />}
        </div>

        {/* Tier picker — visible in both modes so partners can unlock margin preview in wholesale too */}
        <div className="grid grid-cols-2 md:grid-cols-4 border mi-line-strong mb-3">
          {TIERS.map((t, i) => {
            const locked = TIER_RANK[t.id] > TIER_RANK[entitledTier];
            return (
              <button
                key={t.id}
                onClick={() => pickTier(t.id)}
                data-testid={`${TID.wrapTierSelect}-${t.id}`}
                data-locked={locked ? 'true' : 'false'}
                aria-pressed={tier === t.id}
                className={`p-4 lg:p-5 text-left transition-colors ${
                  tier === t.id ? 'bg-[#E63946] text-white'
                    : upgradeFor === t.id ? 'bg-white/10 text-white'
                    : 'bg-black/60 hover:bg-white/5 text-white/85'
                } ${i < 3 ? 'md:border-r mi-line-strong' : ''} ${i < 2 ? 'border-b md:border-b-0 mi-line-strong' : ''}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="mi-headline text-2xl">{t.label}</span>
                  {locked && <Lock className="w-4 h-4 opacity-80" data-testid={`wrap-tier-lock-${t.id}`} />}
                </div>
                <div className="mi-eyebrow text-[10px] opacity-80">{t.note}</div>
              </button>
            );
          })}
        </div>
        <div className="mb-10">
          <EntitlementBadge entitlement={entitlement} isAuthed={isAuthed} onSignIn={() => setSignInOpen(true)} />
          {upgradeFor && (
            <UpgradePrompt
              tierId={upgradeFor}
              isAuthed={isAuthed}
              onSignIn={() => setSignInOpen(true)}
              onClose={() => setUpgradeFor(null)}
            />
          )}
        </div>

        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6">
          {/* Inputs */}
          <div className="mi-surface p-6 lg:p-8">
            <div className="mi-eyebrow mb-4">// INPUTS</div>

            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Surface / Vehicle">
                <select
                  data-testid={TID.wrapPresetSelect}
                  value={preset}
                  onChange={(e) => setPreset(e.target.value)}
                  className="mi-input"
                >
                  {presetsList.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </Field>

              {!isWholesale ? (
                <Field label="Material">
                  <select
                    data-testid={TID.wrapMaterialSelect}
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                    className="mi-input"
                  >
                    {materialsList.map((m) => (
                      <option key={m.key} value={m.key}>{`${m.label} — $${m.cost_per_sqft.toFixed(2)}/sqft`}</option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label={isBox ? 'Linear feet' : (isSqftPreset ? 'Sqft' : 'SKU')}>
                  {isBox ? (
                    <input
                      type="number" min={10} max={53} step={1}
                      value={linearFt} onChange={(e) => setLinearFt(parseFloat(e.target.value || '20'))}
                      data-testid={TID.wrapLinearFt} className="mi-input"
                    />
                  ) : isSqftPreset ? (
                    <input
                      type="number" min={10} max={5000} step={10}
                      value={sqftOverride} onChange={(e) => setSqftOverride(parseFloat(e.target.value || '100'))}
                      className="mi-input"
                    />
                  ) : (
                    <select
                      data-testid="wrap-sku-select"
                      value={skuOverride || (result?.sku_code || '')}
                      onChange={(e) => setSkuOverride(e.target.value)}
                      className="mi-input"
                    >
                      <option value="">Auto ({result?.sku_code || '—'})</option>
                      {wholesaleSkus.map((s) => (
                        <option key={s.code} value={s.code}>{`${s.code} — ${s.label}`}</option>
                      ))}
                    </select>
                  )}
                </Field>
              )}

              <Field label="Coverage">
                <div className="grid grid-cols-1 gap-2" data-testid={TID.wrapCoverage}>
                  {COVERAGE_OPTS.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setCoverage(c.id)}
                      className={`w-full flex items-center justify-between gap-4 px-4 py-3 border transition-colors ${
                        coverage === c.id
                          ? 'border-[#E63946] bg-[#E63946]/10 text-white shadow-[inset_0_0_0_1px_rgba(230,57,70,0.35)]'
                          : 'border-white/10 bg-[#0A0A0A] text-white/80 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <span className="mi-headline text-lg">{c.label}</span>
                      <span className="mi-eyebrow text-[10px] opacity-70 text-right">{c.desc}</span>
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Finish">
                <div className="grid grid-cols-3 gap-2" data-testid={TID.wrapFinish}>
                  {FINISH_OPTS.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFinish(f.id)}
                      className={`px-2 py-2 border text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${
                        finish === f.id
                          ? 'border-[#E63946] bg-[#E63946]/10 text-white'
                          : 'border-white/10 bg-[#0A0A0A] text-white/70 hover:border-white/30'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </Field>

              {!isWholesale ? (
                <Field label={`Shop labor rate — $${isFree ? 95 : labor}/hr${isFree ? ' · locked' : ''}`}>
                  <input
                    type="range" min={60} max={180} step={5}
                    value={isFree ? 95 : labor}
                    onChange={(e) => !isFree && setLabor(parseInt(e.target.value, 10))}
                    disabled={isFree}
                    data-testid={TID.wrapLaborSlider}
                    className="w-full accent-[#E63946] disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                  <div className="flex justify-between text-[10px] mi-eyebrow opacity-70 mt-1">
                    <span>$60</span><span>$120</span><span>$180</span>
                  </div>
                  {isFree && (
                    <div className="mt-1.5 text-[10px] text-white/50 inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Custom labor rate unlocks on Pro.
                    </div>
                  )}
                </Field>
              ) : (
                <Field label={`Difficulty modifier — +${difficulty}%`}>
                  <input
                    type="range" min={0} max={25} step={1}
                    value={difficulty}
                    onChange={(e) => setDifficulty(parseFloat(e.target.value))}
                    data-testid={TID.wrapDifficulty}
                    className="w-full accent-[#E63946]"
                  />
                  <div className="flex justify-between text-[10px] mi-eyebrow opacity-70 mt-1">
                    <span>Base</span><span>+12%</span><span>+25%</span>
                  </div>
                </Field>
              )}

              <Field label="Quantity">
                <input
                  type="number" min={1} max={100}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || '1', 10)))}
                  data-testid={TID.wrapQuantity}
                  className="mi-input"
                />
              </Field>
            </div>

            {/* Retail Pro controls */}
            {!isWholesale && (
              <div className={`mt-6 border mi-line-strong p-5 relative ${!isPro ? 'mi-locked' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="mi-eyebrow flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[#E63946]" /> // PRO CONTROLS
                  </div>
                  {!isPro && (
                    <button onClick={openMembership} data-testid={TID.wrapUpgradeBtn} className="mi-tag mi-tag-red hover:opacity-80">
                      <Lock className="w-3 h-3" /> UPGRADE — 7 DAY TRIAL
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <Field label={`Complexity ×${complexity.toFixed(2)}`}>
                    <input
                      type="range" min={0.5} max={2.5} step={0.05}
                      value={complexity}
                      onChange={(e) => setComplexity(parseFloat(e.target.value))}
                      data-testid={TID.wrapComplexity} disabled={!isPro}
                      className="w-full accent-[#E63946] disabled:opacity-40"
                    />
                    <div className="flex justify-between text-[10px] mi-eyebrow opacity-70 mt-1">
                      <span>Simple</span><span>Standard</span><span>Complex</span>
                    </div>
                  </Field>

                  <Field label={`Margin +${margin}%`}>
                    <input
                      type="range" min={0} max={200} step={5}
                      value={margin}
                      onChange={(e) => setMargin(parseInt(e.target.value, 10))}
                      data-testid={TID.wrapMargin} disabled={!isPro}
                      className="w-full accent-[#E63946] disabled:opacity-40"
                    />
                    <div className="flex justify-between text-[10px] mi-eyebrow opacity-70 mt-1">
                      <span>0%</span><span>100%</span><span>200%</span>
                    </div>
                  </Field>
                </div>
              </div>
            )}

            {/* Wholesale: exact vehicle from PVO sqft database */}
            {isWholesale && (
              <div className={`mt-6 border p-5 ${showVehicleDb ? 'border-[#E63946]/40 bg-[#E63946]/[0.03]' : 'mi-line-strong'}`} data-testid="wrap-vehicle-db">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-[#E63946]" />
                    <div className="mi-eyebrow text-[#E63946]">// EXACT VEHICLE · PVO SQFT DATABASE</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVehicleDb((v) => !v)}
                    data-testid="wrap-vehicle-db-toggle"
                    className={`px-3 py-1.5 text-[11px] mi-headline border transition-colors ${showVehicleDb ? 'bg-[#E63946] border-[#E63946] text-white' : 'mi-line-strong text-white/70 hover:text-white'}`}
                  >
                    {showVehicleDb ? 'ON — pricing by real sqft' : 'Use Year / Make / Model'}
                  </button>
                </div>
                {showVehicleDb && (
                  <div className="mt-4">
                    <VehiclePicker onChange={setVehicleSel} testIdPrefix="wrap-vp" />
                    <div className="mt-3 text-[11px] text-white/50 leading-relaxed">
                      When a vehicle is selected, the quote prices your real panel sqft (× coverage) at the per-sqft rate —
                      <span className="text-white/80"> SQF-COM</span> for solid / printed, <span className="text-white/80">SQF-CLR</span> for color-change finishes.
                      Toggle <span className="text-white/80">No roof</span> to drop the roof panel from the total.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Wholesale Profit Preview */}
            {isWholesale && (
              <div className="mt-6 border border-[#E63946]/40 p-5 bg-[#E63946]/[0.03]">
                <div className="flex items-center gap-2 mb-4">
                  <Percent className="w-4 h-4 text-[#E63946]" />
                  <div className="mi-eyebrow text-[#E63946]">// PROFIT PREVIEW</div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Your print cost ($ / unit)">
                    <input
                      type="number" min={0} step={25}
                      value={printCost}
                      onChange={(e) => setPrintCost(parseFloat(e.target.value || '0'))}
                      data-testid={TID.wrapPrintCost}
                      className="mi-input"
                    />
                  </Field>
                  <Field label="Retail sell price ($ / unit)">
                    <input
                      type="number" min={0} step={50}
                      value={retailPrice}
                      onChange={(e) => setRetailPrice(parseFloat(e.target.value || '0'))}
                      data-testid={TID.wrapRetailPrice}
                      className="mi-input"
                    />
                  </Field>
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={isAuthed ? doCalculate : () => setSignInOpen(true)} data-testid={TID.wrapCalcBtn} className="mi-btn-red">
                {isAuthed ? (
                  <><RefreshCw className={`w-4 h-4 ${calculating ? 'animate-spin' : ''}`} /> Recalculate</>
                ) : (
                  <><LogIn className="w-4 h-4" /> Sign in to run</>
                )}
              </button>
              {/* Save Quote — Starter+ (Guest sees this but it prompts upgrade). Hidden entirely when signed out. */}
              {isAuthed && (
              <button
                onClick={() => {
                  if (isGuest) {
                    toast.info('Save & email is a Starter feature — free to upgrade.');
                    openMembership();
                    return;
                  }
                  saveQuote();
                }}
                data-testid="wrap-save-quote-btn"
                className={`mi-btn-ghost ${isGuest ? 'opacity-70' : ''}`}
              >
                {isGuest ? <Lock className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                Save & email quote
              </button>
              )}
              {isAuthed && (!isWholesale ? (
                <button
                  onClick={() => { if (!isPro) { setUpgradeFor('pro'); toast.info('Branded PDF is a Pro feature'); return; } downloadProposal(); }}
                  data-testid="wrap-export-pdf-retail"
                  className="mi-btn-ghost"
                >
                  {isPro ? <Download className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Export PDF
                </button>
              ) : (
                <>
                  <button
                    onClick={() => { if (!isPro) { setUpgradeFor('pro'); toast.info('Proposal PDF is a Pro feature'); return; } downloadProposal(); }}
                    data-testid="wrap-export-pdf-wholesale"
                    className="mi-btn-ghost"
                  >
                    {isPro ? <Download className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Export proposal PDF
                  </button>
                  <a href="/rates" className="mi-btn-ghost">
                    View full rate card <ArrowRight className="w-4 h-4" />
                  </a>
                </>
              ))}
            </div>
          </div>

          {/* Result panel */}
          <div className="mi-surface p-6 lg:p-8 mi-carbon relative">
            <div className="bg-black/85 -m-6 lg:-m-8 p-6 lg:p-8 h-full">
              <div className="mi-eyebrow mb-4 text-[#E63946]">// LIVE QUOTE {isWholesale && <span className="text-white/50">· WHOLESALE</span>}</div>
              {!isAuthed ? (
                <SignInGate onOpen={() => setSignInOpen(true)} />
              ) : result ? (
                (result.mode === 'wholesale' || result.sku_code) ? <WholesaleResult r={result} /> : <RetailResult r={result} />
              ) : (
                <div className="text-white/50">Adjust inputs to see live pricing.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SignInModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        title="Unlock the Wrapulator"
      />

      <style>{`
        .mi-input {
          width: 100%;
          background: #0A0A0A;
          color: #fff;
          border: 1px solid rgba(255,255,255,0.12);
          padding: 0.65rem 0.9rem;
          border-radius: 2px;
          font-family: 'Exo 2', sans-serif;
          font-size: 0.9rem;
        }
        .mi-input:hover { border-color: rgba(255,255,255,0.22); }
        /* Native <select> — force a bright, visible chevron so the dropdown reads
           obviously as a dropdown against the dark background. */
        select.mi-input {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          padding-right: 2.5rem;
          background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23E63946' stroke-width='2.5' stroke-linecap='square' stroke-linejoin='miter'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.85rem center;
          background-size: 14px 14px;
          cursor: pointer;
        }
        select.mi-input:hover { border-color: #E63946; }
        select.mi-input:focus { border-color: #E63946 !important; }
        select.mi-input option { background: #0A0A0A; color: #fff; }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mi-eyebrow mb-2 text-white/70">{label}</div>
      {children}
    </label>
  );
}

// -------------------------------------------------------------------
// Bulk-tier badge — lights up when cumulative sqft crosses 1,500
// -------------------------------------------------------------------
const BULK_THRESHOLD = 1500;

function _wholesaleCumulativeSqft(r) {
  if (!r) return 0;
  const qty = Math.max(1, r.quantity || 1);
  // basis_label starts with "N sqft × …" for SQF SKUs
  if (r.sku_unit === "sqft") {
    const m = /([\d.]+)\s*sqft/i.exec(r.basis_label || "");
    if (m) return parseFloat(m[1]) * qty;
  }
  // For FLT vehicle tiers, estimate coverage from Motor iNKS averages so partners still get a signal.
  const flatEquiv = { "FLT-SM": 220, "FLT-MD": 320, "FLT-LG": 420, "FLT-BOX": 480 };
  if (flatEquiv[r.sku_code]) return flatEquiv[r.sku_code] * qty;
  return 0;
}

function BulkTierBadge({ result }) {
  const sqft = _wholesaleCumulativeSqft(result);
  const unlocked = sqft >= BULK_THRESHOLD;
  const pct = Math.min(100, Math.round((sqft / BULK_THRESHOLD) * 100));
  const remaining = Math.max(0, BULK_THRESHOLD - sqft);

  return (
    <div
      data-testid={TID.wrapBulkBadge}
      data-unlocked={unlocked ? "1" : "0"}
      className={`inline-flex items-center gap-2.5 px-3 py-1.5 border transition-colors ${
        unlocked
          ? "border-[#E63946] bg-[#E63946] text-white animate-pulse"
          : "border-white/15 bg-black/60 text-white/70"
      }`}
      title={unlocked
        ? "Bulk tier active — negotiated deductions unlock past 1,500 sqft"
        : `${Math.round(sqft)} / ${BULK_THRESHOLD} sqft toward bulk tier`}
    >
      <Layers className={`w-3.5 h-3.5 ${unlocked ? "text-white" : "text-[#E63946]"}`} />
      <span className="mi-headline text-xs uppercase tracking-widest">
        {unlocked ? "BULK TIER UNLOCKED" : `${Math.round(sqft)} / ${BULK_THRESHOLD} SQFT`}
      </span>
      {!unlocked && sqft > 0 && (
        <span className="relative w-14 h-1.5 bg-white/10 overflow-hidden">
          <span className="absolute inset-y-0 left-0 bg-[#E63946]" style={{ width: `${pct}%` }} />
        </span>
      )}
      {!unlocked && sqft > 0 && (
        <span className="mi-eyebrow text-[9px] text-white/50">{Math.round(remaining)} to go</span>
      )}
    </div>
  );
}

function Row({ k, v, strong }) {
  return (
    <div className={`flex items-center justify-between py-2 ${strong ? 'border-t mi-line-strong mt-2 pt-3' : ''}`}>
      <span className={`text-sm ${strong ? 'mi-eyebrow' : 'text-white/60'}`}>{k}</span>
      <span className={`font-mono-data text-sm ${strong ? 'text-white font-bold text-base' : 'text-white/85'}`}>{v}</span>
    </div>
  );
}

function SignInGate({ onOpen }) {
  return (
    <div data-testid="wrap-signin-gate" className="min-h-[520px] flex flex-col items-center justify-center text-center px-4 py-8">
      <div className="w-14 h-14 border-2 border-[#E63946] flex items-center justify-center mb-5">
        <Lock className="w-5 h-5 text-[#E63946]" />
      </div>
      <div className="mi-eyebrow text-[#E63946] mb-3">// SIGN-IN REQUIRED</div>
      <div className="mi-headline text-3xl md:text-4xl leading-tight max-w-md mb-3">
        Sign in to run the Wrapulator.
      </div>
      <div className="text-white/60 text-sm max-w-md mb-8 leading-relaxed">
        Pick any tier, vehicle and material on the left — but the numbers stay hidden until you sign in.
        Free price ranges on Guest tier. 30 seconds. No password.
      </div>
      <button
        onClick={onOpen}
        data-testid="wrap-signin-gate-btn"
        className="mi-btn-red text-sm"
      >
        <LogIn className="w-4 h-4" /> Sign in to unlock
      </button>
      <div className="mt-6 grid grid-cols-2 gap-3 text-[10px] mi-eyebrow text-white/50 max-w-md w-full">
        <div className="border mi-line p-3">
          <div className="text-white/85 mb-0.5">RETAIL</div>
          Ballpark quote · Save & email
        </div>
        <div className="border mi-line p-3">
          <div className="text-white/85 mb-0.5">PARTNERS</div>
          Wholesale rates · PDF · Margin
        </div>
      </div>
    </div>
  );
}

function RetailResult({ r }) {
  const guest = r.guest_ballpark;
  return (
    <>
      <div className="mb-6">
        <div className="mi-eyebrow text-white/50 mb-2">
          {guest ? 'GUEST · PRICE RANGE' : 'GRAND TOTAL'}
        </div>
        <div data-testid={TID.wrapGrandTotal} className={`mi-headline text-white ${guest ? 'text-5xl md:text-6xl' : 'text-6xl md:text-7xl'}`}>
          {guest ? fmtRange(r.price_range_low, r.price_range_high) : fmtUSD(r.grand_total)}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <span className="mi-tag">{r.preset}</span>
          <span className="mi-tag">{r.material}</span>
          <span className="mi-tag">{r.coverage} · {r.finish}</span>
          {r.quantity > 1 && <span className="mi-tag">×{r.quantity}</span>}
        </div>
      </div>

      {guest ? (
        <div className="p-4 border border-[#E63946]/40 bg-[#E63946]/[0.04] text-white/80 text-sm leading-relaxed">
          <div className="mi-eyebrow text-[#E63946] mb-2 flex items-center gap-2">
            <Lock className="w-3 h-3" /> // GUEST TIER
          </div>
          {r.ballpark_note || 'Guest pricing is shown as a range. Starter members see the exact quote and full material · labor · finish breakdown.'}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-white/60"><Lock className="w-3 h-3" /> Exact quote (Starter)</div>
            <div className="flex items-center gap-1.5 text-white/60"><Lock className="w-3 h-3" /> Line-item breakdown (Starter)</div>
            <div className="flex items-center gap-1.5 text-white/60"><Lock className="w-3 h-3" /> Custom labor rate (Pro)</div>
            <div className="flex items-center gap-1.5 text-white/60"><Lock className="w-3 h-3" /> Branded PDF (Pro)</div>
          </div>
        </div>
      ) : (
        <>
          <div className="mi-sep mb-4" />
          <Row k="Base sqft"          v={r.base_sqft} />
          <Row k="Effective sqft"     v={r.effective_sqft} />
          <Row k="+ Waste allowance"  v={r.sqft_with_waste} />
          <Row k="Material cost"      v={fmtUSD(r.material_cost)} />
          <Row k="Labor hours"        v={`${r.labor_hours} hr`} />
          <Row k="Labor cost"         v={fmtUSD(r.labor_cost)} />
          <Row k="Finish upcharge"    v={fmtUSD(r.finish_upcharge)} />
          {r.complexity_applied != null && r.complexity_applied !== 1 && <Row k="Complexity" v={`× ${r.complexity_applied.toFixed(2)}`} />}
          {r.margin_applied_percent > 0 && <Row k="Margin" v={`+${r.margin_applied_percent.toFixed(0)}%`} />}
          <Row k="Per unit" v={fmtUSD(r.total_per_unit)} strong />
          {r.locked_features_note && (
            <div className="mt-5 p-3 border border-[#E63946]/40 text-xs text-[#ffb4bb] flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {r.locked_features_note}
            </div>
          )}
          <div className="mt-6 p-4 border mi-line bg-black/40">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[#E63946]" />
              <span className="mi-eyebrow">EST TURNAROUND</span>
            </div>
            <div className="mi-headline text-2xl">
              {Math.max(2, Math.ceil((r.labor_hours || 0) / 8))} days · {r.quantity} unit{r.quantity > 1 ? 's' : ''}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function WholesaleResult({ r }) {
  const p = r.profit_preview;
  const isGuest = r.guest_ballpark || r.tier === 'guest';
  const hasNumbers = !!(p && p.retail_sell_price_per_unit > 0);
  return (
    <>
      <div className="mb-5">
        <div className="mi-eyebrow text-white/50 mb-2">
          MOTOR iNKS LABOR · <span className="text-[#E63946]">{r.sku_code}</span>{isGuest && ' · GUEST RANGE'}
        </div>
        <div data-testid={TID.wrapGrandTotal} className="mi-headline text-5xl md:text-6xl text-white leading-none">
          {isGuest ? fmtRange(r.price_range_low, r.price_range_high) : fmtRange(r.grand_total_low, r.grand_total_high)}
        </div>
        <div className="mt-2 text-white/50 text-xs mi-eyebrow">
          {r.basis_label} {r.quantity > 1 && `· ×${r.quantity}`}
        </div>
      </div>

      <div className="mi-sep mb-3" />
      {r.vehicle && (
        <div className="mb-3 p-3 border border-[#E63946]/40 bg-[#E63946]/[0.05]" data-testid="wrap-result-vehicle">
          <div className="mi-eyebrow text-[10px] text-[#E63946] mb-1 flex items-center gap-1.5"><Car className="w-3 h-3" /> {r.vehicle.label}</div>
          <div className="font-mono-data text-xs text-white/80">
            {r.vehicle.sqft_used} sqft {r.vehicle.include_roof ? 'with roof' : 'roof excluded'}
            {r.vehicle.has_roof && !r.vehicle.include_roof && <span className="text-white/40"> · roof {r.vehicle.roof_sqft} sqft dropped</span>}
            {r.vehicle.estimated && <span className="text-yellow-400/90"> · estimated</span>}
          </div>
        </div>
      )}
      <Row k="Service"       v={r.sku_label} />
      <Row k="Timeline"      v={r.sku_timeline} />
      {r.difficulty_modifier_percent > 0 && <Row k="Difficulty" v={`+${r.difficulty_modifier_percent}%`} />}
      <Row k="Labor / unit"  v={fmtRange(r.labor_low_per_unit, r.labor_high_per_unit)} />
      <Row k="Labor total"   v={fmtRange(r.labor_low_total, r.labor_high_total)} strong />

      {/* Margin panel — always rendered so partners can see the value slot. */}
      <div className={`mt-6 p-5 border ${hasNumbers ? 'border-[#E63946] bg-[#E63946]/[0.06]' : 'mi-line bg-black/40'}`}>
        <div className="mi-eyebrow text-[#E63946] mb-3 flex items-center justify-between">
          <span>// YOUR MARGIN</span>
          {isGuest && (
            <span className="mi-tag text-[9px] inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> STARTER+
            </span>
          )}
        </div>

        {hasNumbers ? (
          <>
            <div className="mi-headline text-4xl md:text-5xl text-white mb-1" data-testid={TID.wrapProfitMargin}>
              {fmtRange(p.margin_low_per_unit, p.margin_high_per_unit)}
            </div>
            <div className="text-white/50 text-xs mi-eyebrow mb-4">
              {p.margin_pct_low === p.margin_pct_high
                ? `${p.margin_pct_low}% MARGIN`
                : `${p.margin_pct_low}% – ${p.margin_pct_high}% MARGIN`} · PER UNIT
            </div>
            <div className="space-y-1 font-mono-data text-xs text-white/70">
              <div className="flex justify-between"><span>Your retail sell</span><span>{fmtUSD(p.retail_sell_price_per_unit)}</span></div>
              <div className="flex justify-between"><span>− Your print cost</span><span>{fmtUSD(p.partner_print_cost_per_unit)}</span></div>
              <div className="flex justify-between"><span>− Our labor (low-high)</span><span>{fmtRange(p.our_labor_low_per_unit, p.our_labor_high_per_unit)}</span></div>
            </div>
            {r.quantity > 1 && (
              <div className="mt-3 pt-3 border-t mi-line-strong flex justify-between font-mono-data text-sm">
                <span className="text-white/70">Total profit ×{r.quantity}</span>
                <span className="text-white font-bold">{fmtRange(p.margin_low_total, p.margin_high_total)}</span>
              </div>
            )}
          </>
        ) : (
          // Placeholder — no retail sell price entered OR Guest tier locked
          <>
            <div className="mi-headline text-4xl md:text-5xl text-white/25 mb-1 font-mono-data" data-testid={TID.wrapProfitMargin}>
              $ ? / ?
            </div>
            <div className="text-white/40 text-xs mi-eyebrow mb-4">? % MARGIN · PER UNIT</div>
            <div className="space-y-1 font-mono-data text-xs text-white/40">
              <div className="flex justify-between"><span>Your retail sell</span><span>$?</span></div>
              <div className="flex justify-between"><span>− Your print cost</span><span>$?</span></div>
              <div className="flex justify-between">
                <span>− Our labor (low-high)</span>
                <span className="text-white/60">{fmtRange(r.labor_low_per_unit, r.labor_high_per_unit)}</span>
              </div>
            </div>
            <div className="mt-4 p-3 border mi-line bg-black/60 text-xs text-white/70 leading-relaxed">
              {isGuest
                ? <>Margin preview is a <strong className="text-[#E63946]">Starter</strong> feature. Upgrade (or redeem a trial pass) to unlock the full profit math on every quote.</>
                : <>Enter your <strong className="text-white">Retail sell price</strong> in the <span className="text-[#E63946]">// PROFIT PREVIEW</span> panel to the left — the margin, %, and total profit will populate here instantly.</>}
            </div>
          </>
        )}
      </div>

      <div className="mt-5 text-[11px] mi-eyebrow text-white/40">
        {r.media_note}
      </div>
    </>
  );
}
